/**
 * The full speaking order, rendered identically in the panel and on stage
 * (BUILD-SPEC.md §7): past speakers greyed with time used, the current
 * speaker highlighted, upcoming plain, plus text markers for "left the
 * meeting", "rejoined", and "joined late" — never colour alone.
 */
import { makeStyles, mergeClasses, tokens, Text } from "@fluentui/react-components";
import type { Session } from "../engine";
import { elapsedFor } from "../engine";
import { formatDuration } from "./format";

const useStyles = makeStyles({
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  done: {
    color: tokens.colorNeutralForeground3,
  },
  current: {
    backgroundColor: tokens.colorBrandBackground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  upcoming: {
    color: tokens.colorNeutralForeground1,
  },
  left: {
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
  marker: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
});

export interface QueueProps {
  session: Session;
  now: number;
}

export function Queue({ session, now }: QueueProps) {
  const styles = useStyles();
  const seenUserIds = new Set<string>();

  return (
    <ol className={styles.list} aria-label="Speaking order">
      {session.queue.map((entry, i) => {
        const isCurrent = i === session.idx && entry.status === "speaking";
        const isRejoin = entry.userId !== null && seenUserIds.has(entry.userId);
        if (entry.userId !== null) seenUserIds.add(entry.userId);

        const rowClass =
          entry.status === "left"
            ? styles.left
            : entry.status === "done"
              ? styles.done
              : isCurrent
                ? styles.current
                : styles.upcoming;

        const timeLabel =
          entry.status === "done" || entry.status === "left"
            ? formatDuration(entry.spentSeconds)
            : isCurrent
              ? formatDuration(elapsedFor(session, i, now))
              : null;

        return (
          <li key={`${entry.key}-${i}`} className={mergeClasses(styles.row, rowClass)}>
            <span>
              {entry.displayName}
              {entry.status === "left" && <span className={styles.marker}> — left the meeting</span>}
              {isRejoin && entry.status !== "left" && <span className={styles.marker}> — rejoined</span>}
              {entry.joinedLate && !isRejoin && entry.status !== "left" && (
                <span className={styles.marker}> — joined late</span>
              )}
            </span>
            {timeLabel && <Text>{timeLabel}</Text>}
          </li>
        );
      })}
    </ol>
  );
}
